import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { REPLACE_USER_SOCIALS_MUTATION } from "@/api/mutations/replaceUserSocials";
import {
  ReplaceUserSocialsMutation,
  ReplaceUserSocialsMutationVariables,
  SocialMediaHandleInput,
} from "@/__generated__/graphql";
import { MutationErrorHandler } from "@/util/mutationUtils";

export interface UseReplaceUserSocialsParams {
  onCompleted?: (data: ReplaceUserSocialsMutation) => void;
  onError?: (error: unknown) => void;
}

export interface ReplaceUserSocialsResult {
  success: boolean;
  errors: {
    message: string;
    title: string;
  }[];
}

export interface UseReplaceUserSocialsReturn {
  loading: boolean;
  error: unknown;
}

export interface ReplaceUserSocialsInput {
  userId: string;
  socials: SocialMediaHandleInput[];
}

export const useReplaceUserSocials = ({
  onCompleted,
  onError,
}: UseReplaceUserSocialsParams = {}): [
  (input: ReplaceUserSocialsInput) => Promise<ReplaceUserSocialsResult>,
  UseReplaceUserSocialsReturn,
] => {
  const [replaceUserSocialsMutation, { loading, error }] = useMutation<
    ReplaceUserSocialsMutation,
    ReplaceUserSocialsMutationVariables
  >(REPLACE_USER_SOCIALS_MUTATION, {
    onCompleted,
    onError,
    refetchQueries: ["MyProfile", "CurrentUser"],
    awaitRefetchQueries: true,
  });

  const replaceUserSocials = useCallback(
    async (
      input: ReplaceUserSocialsInput,
    ): Promise<ReplaceUserSocialsResult> => {
      try {
        const result = await replaceUserSocialsMutation({
          variables: {
            userId: input.userId,
            socials: input.socials,
          },
        });

        const errorHandler = MutationErrorHandler.fromResult(
          result,
          result.errors,
        );
        return errorHandler.toResult();
      } catch (error) {
        return {
          success: false,
          errors: [
            {
              message: error instanceof Error ? error.message : "Unknown error",
              title: "Mutation Error",
            },
          ],
        };
      }
    },
    [replaceUserSocialsMutation],
  );

  return [replaceUserSocials, { loading, error }];
};
